/**
 * csv（02 5.3.20）の型と入力検証：出力ジョブ（API-CSV-01〜03）・取込（API-CSV-04〜08）。
 * - 出力の filters はその一覧の API のクエリと同じ名前・同じ検証（検証は API の service で一覧のスキーマを使う）
 * - columns は列の選択ができる出力（R-101・102・106・107）だけ。空の配列は MSG-V-070、表に無い列名は 400
 * - 取込のファイルは .csv・10MB まで（形式・大きさの誤りは FILE_003／FILE_004：service）
 */
import { z } from 'zod';
import {
  COLUMN_SELECTABLE_EXPORT_TYPES,
  EXPORT_COLUMNS,
  EXPORT_TYPE_VALUES,
  IMPORT_TYPE_VALUES,
  REPORT_SECTION_VALUES,
} from '../csv/columns';
import type { ColumnSelectableExportType } from '../csv/columns';
import { compareDate, isValidJstDate } from '../date/jst';
import { CUSTOMER_TYPE_VALUES } from '../enums/customer-type';
import { JOB_STATUS_VALUES } from '../enums/job-status';
import { JOB_TYPE_VALUES } from '../enums/job-type';
import { zCode, zDate, zEnum, zRequiredText, zSystemCode, zYearMonth } from './common.schema';
import { MAX_PER_PAGE } from './list-query.schema';

/** R-112〜114 の条件（02 5.3.20 report_parameters） */
export const ReportParametersSchema = z
  .object({
    from: zDate,
    to: zDate,
    sales_user_code: zSystemCode.optional(),
    sections: z
      .array(zEnum(REPORT_SECTION_VALUES))
      .min(1, 'MSG-V-070')
      .refine((values) => new Set(values).size === values.length, 'MSG-V-020')
      .optional(),
  })
  .strict()
  // 項目の誤りがあっても refine は呼ばれるため、日付の形を確かめてから比べる（from=abc で compareDate が RangeError：238 N-06）
  .refine(
    (value) => !isValidJstDate(value.from) || !isValidJstDate(value.to) || compareDate(value.to, value.from) >= 0,
    { message: 'FE-MSG-V-006', path: ['to'] },
  );

const REPORT_EXPORT_TYPES: ReadonlySet<string> = new Set(['reportDetail', 'salesReportExcel', 'salesReportPdf']);

/** API-CSV-01 出力ジョブの登録 */
export const ExportJobCreateSchema = z
  .object({
    export_type: zEnum(EXPORT_TYPE_VALUES),
    filters: z.record(z.unknown()).optional(),
    columns: z.array(z.string({ message: 'MSG-V-020' }).min(1, 'MSG-V-020')).optional(),
    sort: z.string().trim().min(1).max(100).optional(),
    report_parameters: ReportParametersSchema.optional(),
    year_month: zYearMonth.optional(),
    form_code: zCode.optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.columns !== undefined) {
      if (!(COLUMN_SELECTABLE_EXPORT_TYPES as readonly string[]).includes(value.export_type)) {
        context.addIssue({ code: 'custom', path: ['columns'], message: 'MSG-V-020' });
      } else if (value.columns.length === 0) {
        context.addIssue({ code: 'custom', path: ['columns'], message: 'MSG-V-070' });
      } else {
        const allowed = new Set<string>(EXPORT_COLUMNS[value.export_type as ColumnSelectableExportType]);
        const is_all_known = value.columns.every((name) => allowed.has(name));
        if (!is_all_known || new Set(value.columns).size !== value.columns.length) {
          context.addIssue({ code: 'custom', path: ['columns'], message: 'MSG-V-020' });
        }
      }
    }
    if (REPORT_EXPORT_TYPES.has(value.export_type) && !value.report_parameters) {
      context.addIssue({ code: 'custom', path: ['report_parameters'], message: 'MSG-V-020' });
    }
    if (value.export_type === 'monthlyChecklist' && !value.year_month) {
      context.addIssue({ code: 'custom', path: ['year_month'], message: 'MSG-V-020' });
    }
    if (value.export_type === 'formSubmissionList' && !value.form_code) {
      context.addIssue({ code: 'custom', path: ['form_code'], message: 'MSG-V-020' });
    }
  });

export const JobCreatedSchema = z.object({ job_code: z.string(), job_status: z.enum(JOB_STATUS_VALUES) });

/** 型 Job（02 5.3.20） */
export const JobSchema = z.object({
  job_code: z.string(),
  job_type: z.enum(JOB_TYPE_VALUES),
  job_status: z.enum(JOB_STATUS_VALUES),
  export_type: z.string().nullable(),
  import_type: z.string().nullable(),
  customer_type: z.string().nullable(),
  total_row_count: z.number().int().nullable(),
  error_row_count: z.number().int().nullable(),
  success_row_count: z.number().int().nullable(),
  result_file_name: z.string().nullable(),
  error_code: z.string().nullable(),
  requested_by: z.string(),
  created_at: z.string(),
  finished_at: z.string().nullable(),
});

export const DownloadUrlSchema = z.object({
  download_url: z.string(),
  expires_at: z.string(),
  file_name: z.string(),
});

export const JobCodePathSchema = z.object({ job_code: zSystemCode });

// ───────── 取込 ─────────

/**
 * パスの import_type は文字列で受け、決まっていない種類は 404 NOT_FOUND_001（243 IT-414・IT-416：存在しない対象）。
 * 種類の判定は service（isImportType）。長すぎる値だけは入力の誤り（400）
 */
export const ImportTypePathSchema = z.object({ import_type: z.string().min(1, 'MSG-V-020').max(64, 'MSG-V-020') });

export function isImportType(value: string): value is (typeof IMPORT_TYPE_VALUES)[number] {
  return (IMPORT_TYPE_VALUES as readonly string[]).includes(value);
}

const zCustomerTypeOption = zEnum(CUSTOMER_TYPE_VALUES).optional();

/** API-CSV-04 のクエリ（customer のときは customer_type 必須：service） */
export const ImportDefinitionQuerySchema = z.object({ customer_type: zCustomerTypeOption });

/** API-CSV-05 のクエリ */
export const ImportTemplateQuerySchema = z.object({
  customer_type: zCustomerTypeOption,
  with_sample: z
    .enum(['true', 'false'], { message: 'MSG-V-020' })
    .transform((value) => value === 'true')
    .default('false'),
});

export const ImportColumnSchema = z.object({
  column_name: z.string(),
  field: z.string(),
  is_required: z.boolean(),
  aliases: z.array(z.string()),
  description: z.string(),
  example: z.string(),
});

export const ImportDefinitionSchema = z.object({
  import_type: z.enum(IMPORT_TYPE_VALUES),
  customer_type: z.enum(CUSTOMER_TYPE_VALUES).nullable(),
  columns: z.array(ImportColumnSchema),
  max_file_size_bytes: z.number().int(),
  max_row_count: z.number().int(),
});

export const ImportTemplateSchema = z.object({ file_name: z.string(), content_base64: z.string() });

/** API-CSV-06 取込ファイルの署名付き POST */
export const ImportUploadUrlCreateSchema = z
  .object({
    import_type: zEnum(IMPORT_TYPE_VALUES),
    customer_type: zCustomerTypeOption,
    file_name: zRequiredText(255, 'MSG-V-011'),
    file_size_bytes: z.number({ message: 'MSG-V-032' }).int('MSG-V-032').min(1, 'MSG-V-032'),
  })
  .strict();

export const ImportUploadUrlSchema = z.object({
  job_code: z.string(),
  upload_url: z.string(),
  upload_fields: z.record(z.string()),
  expires_at: z.string(),
  /** 保存場所の名前（imports/<job_code>/<送ったファイル名>。upload_fields.key と同じ：243 UT-443・IT-189 の fileKey） */
  file_key: z.string(),
});

/** API-CSV-07 取込の開始（CSV の見出し → field か null） */
export const ImportStartSchema = z
  .object({ column_mapping_map: z.record(z.string().min(1).nullable(), { message: 'MSG-V-020' }) })
  .strict();

/** API-CSV-08 のクエリ */
export const ImportErrorListQuerySchema = z.object({
  format: z.enum(['json', 'csv'], { message: 'MSG-V-020' }).default('json'),
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(MAX_PER_PAGE).default(20),
});

export const ImportErrorSchema = z.object({
  row_number: z.number().int(),
  column_name: z.string(),
  error_message: z.string(),
});

export type ReportParameters = z.infer<typeof ReportParametersSchema>;
export type ExportJobCreateInput = z.infer<typeof ExportJobCreateSchema>;
export type JobCreated = z.infer<typeof JobCreatedSchema>;
export type Job = z.infer<typeof JobSchema>;
export type DownloadUrl = z.infer<typeof DownloadUrlSchema>;
export type ImportDefinition = z.infer<typeof ImportDefinitionSchema>;
export type ImportColumn = z.infer<typeof ImportColumnSchema>;
export type ImportTemplate = z.infer<typeof ImportTemplateSchema>;
export type ImportUploadUrlCreateInput = z.infer<typeof ImportUploadUrlCreateSchema>;
export type ImportUploadUrl = z.infer<typeof ImportUploadUrlSchema>;
export type ImportStartInput = z.infer<typeof ImportStartSchema>;
export type ImportErrorListQuery = z.infer<typeof ImportErrorListQuerySchema>;
export type ImportErrorItem = z.infer<typeof ImportErrorSchema>;
